/**
 * toUtcSeconds — conversor ÚNICO de tiempos a segundos UTC (frontera de datos).
 *
 * Por qué existe: en timeframes diarios+ (1D/1W/1M) los `bar.time` llegan como
 * STRING 'YYYY-MM-DD' (usePublicChartData los convierte para que LWC los trate
 * como business days) y los payloads de backend (/charting/*) traen tiempos en
 * MILISEGUNDOS. Todo matching estricto (`===`, `Set.has`, comparaciones
 * número-vs-string) entre esos mundos falla en silencio y el indicador no
 * pinta nada. Un solo conversor, usado por todos los indicadores en su
 * frontera de datos, elimina la clase entera de bugs.
 *
 * Acepta:
 *   - number en segundos → identidad (redondeado a entero)
 *   - number en milisegundos (heurística: > 1e11 ≈ año 5138 en s / 1973 en ms)
 *   - string 'YYYY-MM-DD' (business day) → medianoche UTC
 *   - string ISO 8601 ('2026-07-30T12:00:00Z', con offset, etc.) → su instante
 *   - objeto business-day de LWC { year, month, day } → medianoche UTC
 * Devuelve null para lo no interpretable (null/undefined/NaN/basura) — el
 * caller omite el item, nunca pinta con un time dudoso.
 *
 * Regla de oro (regresión intradía): en TFs ≤ 4h los tiempos ya son segundos
 * numéricos y este helper es IDENTIDAD para ellos.
 */

const MS_THRESHOLD = 1e11 // > 1e11 s ≈ año 5138; por encima solo pueden ser ms

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

export function toUtcSeconds(time) {
  if (time == null || time === '') return null

  if (typeof time === 'number') {
    if (!Number.isFinite(time)) return null
    if (time > MS_THRESHOLD) return Math.floor(time / 1000)
    return Math.round(time)
  }

  if (typeof time === 'string') {
    const s = time.trim()
    if (DATE_ONLY_RE.test(s)) {
      const ms = Date.parse(`${s}T00:00:00Z`)
      return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
    }
    // Numérica en string ('1753000000') → segundos
    if (/^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s)
      if (!Number.isFinite(n)) return null
      return n > MS_THRESHOLD ? Math.floor(n / 1000) : Math.round(n)
    }
    // ISO 8601 u otro formato parseable por Date
    const ms = Date.parse(s)
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
  }

  // Business-day object de LWC: { year, month (1-12), day }
  if (typeof time === 'object') {
    const { year, month, day } = time
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      const ms = Date.UTC(year, month - 1, day)
      return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
    }
  }

  return null
}
